<template>
  <div class="w-100">
    <!-- Progress Info -->
    <v-row no-gutters class="mb-1 align-center">
      <v-col cols="auto">
        <span style="font-weight: 200; font-size: 14px">
          {{ percentComplete }}% complete<span v-if="percentInProgress > 0"
            >, {{ percentInProgress }}% in progress</span
          >
        </span>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <span style="font-weight: 200; font-size: 14px"> {{ earnedCredits }}/{{ totalCredits }} {{ unit }} </span>
      </v-col>
    </v-row>

    <!-- Progress Bar -->
    <div
      class="d-flex overflow-hidden"
      style="width: 100%; height: 12px; background-color: #e8e8e8; border-radius: 12px; position: relative"
    >
      <!-- Completed (Transcripted + Waiting) - Combined Green Bar -->
      <v-tooltip v-if="completedPercent > 0" location="top">
        <template #activator="{ props }">
          <div
            v-bind="props"
            :style="{
              width: completedPercent + '%',
              height: '100%',
              backgroundColor: '#78BE20',
              borderRadius: inProgressPercent > 0 ? '12px 0 0 12px' : '12px',
              transition: 'width 0.3s ease'
            }"
          />
        </template>
        <span>{{ earnedCredits }} {{ unit }} completed</span>
      </v-tooltip>

      <!-- In Progress (Striped pattern) -->
      <v-tooltip v-if="inProgressPercent > 0" location="top">
        <template #activator="{ props }">
          <div
            v-bind="props"
            :style="{
              width: inProgressPercent + '%',
              height: '100%',
              backgroundColor: '#9e9e9e',
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 255, 255, 0.3) 10px, rgba(255, 255, 255, 0.3) 20px)',
              borderRadius: '0 12px 12px 0',
              transition: 'width 0.3s ease'
            }"
          />
        </template>
        <span>{{ inProgress }} {{ unit }} in-progress</span>
      </v-tooltip>
    </div>

    <!-- Legend (Optional) -->
    <v-row v-if="showLegend" no-gutters class="mt-3">
      <v-col cols="12" class="d-flex flex-wrap" style="gap: 16px">
        <!-- Completed (Transcripted + Waiting) -->
        <div class="d-flex align-center" style="gap: 8px">
          <div style="width: 16px; height: 16px; background-color: #78be20; border-radius: 3px" />
          <span class="text-body-2" style="color: #666666">{{ earnedCredits }} {{ unit }} completed</span>
        </div>

        <!-- In Progress -->
        <div class="d-flex align-center" style="gap: 8px">
          <div
            style="
              width: 16px;
              height: 16px;
              background-color: #9e9e9e;
              background-image: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 5px,
                rgba(255, 255, 255, 0.3) 5px,
                rgba(255, 255, 255, 0.3) 10px
              );
              border-radius: 3px;
            "
          />
          <span class="text-body-2" style="color: #666666">{{ inProgress }} {{ unit }} in-progress</span>
        </div>

        <!-- Remaining -->
        <div class="d-flex align-center" style="gap: 8px">
          <div style="width: 16px; height: 16px; background-color: #e0e0e0; border-radius: 3px" />
          <span class="text-body-2" style="color: #666666">{{ notStarted }} {{ unit }} remaining</span>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ProgressBar',

  props: {
    transcripted: {
      type: Number,
      default: 0
    },
    waiting: {
      type: Number,
      default: 0
    },
    inProgress: {
      type: Number,
      default: 0
    },
    notStarted: {
      type: Number,
      default: 0
    },
    totalCredits: {
      type: Number,
      required: true
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    unit: {
      type: String,
      default: 'credits'
    }
  },

  computed: {
    // Earned credits = only completed/transcripted, NOT in progress
    earnedCredits() {
      return this.transcripted + this.waiting;
    },

    // Percent complete = only completed courses (capped at 100%)
    percentComplete() {
      if (this.totalCredits === 0) return 0;
      const percent = Math.round((this.earnedCredits / this.totalCredits) * 100);
      return Math.min(percent, 100);
    },

    // Percent in progress = currently taking (only if complete < 100%, and combined never exceeds 100%)
    percentInProgress() {
      if (this.totalCredits === 0) return 0;
      const completePct = (this.earnedCredits / this.totalCredits) * 100;
      // If already complete, don't show in-progress
      if (completePct >= 100) return 0;
      const inProgressPct = Math.round((this.inProgress / this.totalCredits) * 100);
      // Cap so complete + inProgress never exceeds 100%
      return Math.min(inProgressPct, 100 - Math.round(completePct));
    },

    // Combined completed percentage (transcripted + waiting) - capped at 100%
    completedPercent() {
      if (this.totalCredits === 0) return 0;
      const percent = (this.earnedCredits / this.totalCredits) * 100;
      return Math.min(percent, 100);
    },

    // In progress only shows if there's room after completed
    inProgressPercent() {
      if (this.totalCredits === 0) return 0;
      const completedPct = (this.earnedCredits / this.totalCredits) * 100;
      // If completed >= 100%, no room for in-progress
      if (completedPct >= 100) return 0;
      const inProgressPct = (this.inProgress / this.totalCredits) * 100;
      // Cap so total doesn't exceed 100%
      return Math.min(inProgressPct, 100 - completedPct);
    },

    remainingPercent() {
      if (this.totalCredits === 0) return 0;
      return (this.notStarted / this.totalCredits) * 100;
    }
  }
});
</script>
